/**
 * NodeCommentThread — threaded comments panel used by the diagram editor.
 *
 * Features (R45 slice 2):
 *   • Flat + threaded (parent_id) replies
 *   • @mentions parsed from text (workspace members list)
 *   • Resolve / unresolve toggle
 *   • Author/admin can edit/delete
 *
 * Props:
 *   diagramId
 *   comments (all comments for the diagram)
 *   setComments (setter)
 *   nodeId    (null → diagram-wide)
 *   nodeLabel (for header)
 *   onClose
 *   members   [{id, name, email}] — for @mention picker (optional)
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Circle, CornerDownRight, MessageSquare, Pencil, Trash2, AtSign } from "lucide-react";

function parseMentions(text, members = []) {
  const out = [];
  const rx = /@([\w.-]+)/g;
  let m;
  while ((m = rx.exec(text)) !== null) {
    const token = m[1].toLowerCase();
    const hit = members.find((u) => (u.email || "").toLowerCase().startsWith(token) || (u.name || "").toLowerCase().replace(/\s+/g, ".").startsWith(token));
    if (hit) out.push(hit.id);
  }
  return Array.from(new Set(out));
}

function renderWithMentions(text) {
  const parts = String(text || "").split(/(@[\w.-]+)/g);
  return parts.map((p, i) => (
    p.startsWith("@") ? (
      <span key={i} className="text-sky-700 bg-sky-50 px-0.5 rounded-sm">{p}</span>
    ) : (
      <span key={i}>{p}</span>
    )
  ));
}

export function NodeCommentThread({ diagramId, comments, setComments, nodeId, nodeLabel, onClose, members = [] }) {
  const { user } = useAuth() || {};
  const scopedNodeId = nodeId || "__diagram__";
  const filtered = useMemo(() => comments.filter((c) => c.node_id === scopedNodeId), [comments, scopedNodeId]);
  const roots = useMemo(() => filtered.filter((c) => !c.parent_id), [filtered]);
  const [showResolved, setShowResolved] = useState(false);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null); // parent_id
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {id, text}

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const mentions = parseMentions(text, members);
      const { data } = await api.post(`/diagrams/${diagramId}/comments`, {
        node_id: scopedNodeId, text: text.trim(), parent_id: replyTo, mentions,
      });
      setComments([...comments, data]);
      setText(""); setReplyTo(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Comment failed"));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing?.text?.trim()) return;
    try {
      const { data } = await api.patch(`/diagrams/${diagramId}/comments/${editing.id}`, { text: editing.text.trim() });
      setComments(comments.map((c) => (c.id === editing.id ? data : c)));
      setEditing(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Edit failed"));
    }
  };

  const del = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/diagrams/${diagramId}/comments/${cid}`);
      setComments(comments.filter((c) => c.id !== cid && c.parent_id !== cid));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    }
  };

  const toggleResolved = async (c) => {
    try {
      const { data } = await api.patch(`/diagrams/${diagramId}/comments/${c.id}`, { resolved: !c.resolved });
      setComments(comments.map((x) => (x.id === c.id ? data : x)));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Update failed"));
    }
  };

  const renderComment = (c, depth = 0) => {
    const replies = filtered.filter((r) => r.parent_id === c.id);
    const canEdit = user?.id === c.author_id;
    const hidden = c.resolved && !showResolved && depth === 0;
    if (hidden) return null;
    return (
      <div key={c.id} className={`border p-2 text-xs ${c.resolved ? "opacity-60" : ""}`} style={{ marginLeft: depth * 12 }} data-testid={`comment-${c.id}`}>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground">{c.author_name}</span>
            <span>{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
            {c.updated_at && c.updated_at !== c.created_at && <span className="italic">(edited)</span>}
          </div>
          <div className="flex items-center gap-1">
            <button className="text-muted-foreground hover:text-foreground" onClick={() => toggleResolved(c)} title={c.resolved ? "Unresolve" : "Resolve"} data-testid={`comment-resolve-${c.id}`}>
              {c.resolved ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Circle className="w-3 h-3" />}
            </button>
            {canEdit && (
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setEditing({ id: c.id, text: c.text })} title="Edit" data-testid={`comment-edit-${c.id}`}><Pencil className="w-3 h-3" /></button>
            )}
            <button className="text-rose-600" onClick={() => del(c.id)} title="Delete" data-testid={`comment-delete-${c.id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        </div>
        {editing?.id === c.id ? (
          <div className="mt-1 space-y-1">
            <Textarea rows={2} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} className="rounded-none text-xs" data-testid={`comment-edit-input-${c.id}`} />
            <div className="flex gap-1">
              <Button size="sm" className="rounded-none h-6" onClick={saveEdit} data-testid={`comment-save-${c.id}`}>Save</Button>
              <Button size="sm" variant="outline" className="rounded-none h-6" onClick={() => setEditing(null)} data-testid={`comment-cancel-edit-${c.id}`}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="mt-1 whitespace-pre-wrap">{renderWithMentions(c.text)}</div>
        )}
        <button className="text-[10px] text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1" onClick={() => { setReplyTo(c.id); setText(""); }} data-testid={`comment-reply-${c.id}`}>
          <CornerDownRight className="w-3 h-3" /> Reply
        </button>
        {replies.map((r) => renderComment(r, depth + 1))}
      </div>
    );
  };

  return (
    <div className="absolute top-2 right-2 w-80 max-h-[75vh] flex flex-col border bg-background shadow-xl z-20" data-testid="node-comment-thread">
      <div className="flex items-center justify-between border-b px-2 py-1.5">
        <div className="text-xs font-semibold flex items-center gap-1">
          <MessageSquare className="w-3.5 h-3.5" />
          {nodeId ? `Node · ${nodeLabel?.slice(0, 22) || "…"}` : "Diagram-wide"}
        </div>
        <div className="flex items-center gap-1">
          <button className={`text-[10px] px-1 border ${showResolved ? "bg-neutral-100" : ""}`} onClick={() => setShowResolved((v) => !v)} title="Toggle resolved" data-testid="comment-toggle-resolved">
            {showResolved ? "Hide resolved" : "Show resolved"}
          </button>
          <button className="text-muted-foreground" onClick={onClose}>✕</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-1.5">
        {roots.length === 0 && <div className="text-[11px] text-muted-foreground">No comments yet. Type below to start the thread.</div>}
        {roots.map((c) => renderComment(c, 0))}
      </div>
      <div className="border-t p-2 space-y-1">
        {replyTo && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            Replying to comment · <button className="underline" onClick={() => setReplyTo(null)}>cancel</button>
          </div>
        )}
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment. Type @ to mention someone."
          className="rounded-none text-xs"
          data-testid="comment-input"
        />
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-muted-foreground flex items-center gap-1"><AtSign className="w-3 h-3" /> Mentions parsed from @name</div>
          <Button size="sm" className="rounded-none h-7" onClick={send} disabled={busy || !text.trim()} data-testid="comment-submit">Post</Button>
        </div>
      </div>
    </div>
  );
}
